import React, { useEffect, useState } from 'react';
import StrainPicker from './StrainPicker';
import SessionCalendar from './SessionCalendar';
import {
  getConsumptionOptions,
  getLogs,
  getReviewedStrains,
  getSessionDates,
  getStatsAccess,
  updateLog,
} from '../api';

function formatWhen(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function formatDateKey(value) {
  if (!value) return '';
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return value;
  return new Date(year, month - 1, day).toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

const BUY_AGAIN_LABELS = {
  1: 'Never Again',
  3: 'Passable but Buyable',
  5: 'Always',
};

const SIDE_EFFECT_SEVERITY_LABELS = {
  1: 'Mild',
  3: 'Moderate',
  5: 'High',
};

const EFFECT_KEYS = [
  'headache',
  'paranoia',
  'dizzy',
  'sleepy',
  'stuffy',
  'cough',
  'dryMouth',
  'dryEye',
  'redEye',
  'anxiety',
];

function isYesEffect(value) {
  return typeof value === 'number' && value >= 1 && value <= 5;
}

function buyAgainHighlight(score, themeColor = 'green') {
  const n = Number(score);
  if (!Number.isInteger(n) || n < 1 || n > 5) return undefined;
  const t = (n - 1) / 4;
  if (buyAgainHighlight._theme !== themeColor) {
    buyAgainHighlight._theme = themeColor;
    buyAgainHighlight._accent = getComputedStyle(document.documentElement)
      .getPropertyValue('--mwb-accent-shadow')
      .trim()
      .split(',')
      .map((part) => Number(part.trim()));
  }
  const accent = buyAgainHighlight._accent;
  const endR = accent[0] || 47;
  const endG = accent[1] || 122;
  const endB = accent[2] || 74;
  const r = Math.round(229 + (endR - 229) * t);
  const g = Math.round(57 + (endG - 57) * t);
  const b = Math.round(53 + (endB - 53) * t);
  const color = `rgb(${r}, ${g}, ${b})`;
  return {
    border: `2px solid ${color}`,
    boxShadow: `0 0 0 3px rgba(${r}, ${g}, ${b}, 0.22)`,
    borderRadius: 12,
    padding: '12px 14px',
    margin: '8px 0',
  };
}

function effectsFromLog(log) {
  const effects = {};
  const severities = log.sideEffectSeverities || {};
  for (const key of EFFECT_KEYS) {
    const score = Number(severities[key] || 0);
    effects[key] = score >= 1 && score <= 5 ? score : log[key] ? 3 : 'no';
  }
  return effects;
}

function draftFromLog(log) {
  return {
    notes: log.notes || '',
    privateNotes: log.privateNotes || '',
    buyAgain: log.buyAgain == null ? 3 : Number(log.buyAgain),
    effects: effectsFromLog(log),
  };
}

function LogSummary({ log }) {
  return (
    <>
      <strong>{log.strainName}</strong>
      {log.brandName ? <span>{log.brandName}</span> : null}
      <span>
        {[
          log.consumptionStyle,
          log.howMethod,
          log.howStyle,
          log.howOther ? `(${log.howOther})` : '',
          log.amount
            ? log.consumptionStyle === 'Smoke'
              ? `${log.amount} g`
              : log.amount
            : '',
          log.strength ? `Strength ${log.strength}` : '',
        ]
          .filter(Boolean)
          .join(' · ')}
      </span>
      {log.enjoyIt === true ? <span>Enjoyed</span> : null}
      {log.enjoyIt === false ? <span>Did not enjoy</span> : null}
      {log.infused === true ? <span>Infused</span> : null}
      {log.buyAgain ? (
        <span>
          Buy again: {log.buyAgain}
          {BUY_AGAIN_LABELS[log.buyAgain] ? ` — ${BUY_AGAIN_LABELS[log.buyAgain]}` : ''}
        </span>
      ) : null}
      <span>
        {log.startingMood} → {log.endingMood}
      </span>
      {log.notes ? <span className="log-notes">{log.notes}</span> : null}
      {log.privateNotes ? (
        <span className="log-notes log-private-notes">Private: {log.privateNotes}</span>
      ) : null}
      <span className="log-effects">
        {(log.sideEffects || []).length
          ? (log.sideEffects || []).join(', ')
          : 'No side effects'}
      </span>
      <span className="log-when">{formatWhen(log.createdAt)}</span>
    </>
  );
}

function Home({
  user,
  onLogout,
  onOpenAccount,
  onOpenConsumption,
  onOpenDashboard,
  onOpenAdminQuery,
  onOpenFriends,
}) {
  const [logs, setLogs] = useState([]);
  const [strains, setStrains] = useState([]);
  const [strain, setStrain] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [sessionDates, setSessionDates] = useState([]);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [sideEffects, setSideEffects] = useState([]);
  const [editingId, setEditingId] = useState('');
  const [draft, setDraft] = useState(null);
  const [editError, setEditError] = useState('');
  const [saving, setSaving] = useState(false);
  const [statsAccess, setStatsAccess] = useState(false);
  const canEdit = !strain;

  useEffect(() => {
    getConsumptionOptions()
      .then((data) => setSideEffects(data.sideEffects || []))
      .catch(() => setSideEffects([]));
  }, []);

  useEffect(() => {
    if (!user?.id) {
      setStrains([]);
      return;
    }
    getReviewedStrains(user.id)
      .then((data) => setStrains(data.strains || []))
      .catch(() => setStrains([]));
  }, [user]);

  useEffect(() => {
    if (!strain) return;
    const stillAvailable = strains.some((item) => item.name === strain);
    if (!stillAvailable) setStrain('');
  }, [strains, strain]);

  useEffect(() => {
    if (!user?.id) {
      setStatsAccess(false);
      return;
    }
    getStatsAccess(user.id)
      .then((data) => setStatsAccess(Boolean(data.allowed)))
      .catch(() => setStatsAccess(false));
  }, [user]);

  useEffect(() => {
    if (!user?.id) {
      setSessionDates([]);
      return;
    }
    getSessionDates(user.id)
      .then((data) => setSessionDates(data.dates || []))
      .catch(() => setSessionDates([]));
  }, [user]);

  useEffect(() => {
    if (!user?.id) return;
    getLogs(user.id, strain, selectedDate)
      .then((data) => setLogs(data.logs || []))
      .catch(() => setLogs([]));
  }, [user, strain, selectedDate]);

  useEffect(() => {
    setEditingId('');
    setDraft(null);
    setEditError('');
  }, [strain, selectedDate]);

  const headingText = selectedDate
    ? `Sessions on ${formatDateKey(selectedDate)}`
    : strain
      ? `Last 10 ${strain} sessions`
      : 'Last 10 sessions';

  const openEditor = (log) => {
    if (!canEdit) return;
    setEditingId(log.id);
    setDraft(draftFromLog(log));
    setEditError('');
  };

  const closeEditor = () => {
    setEditingId('');
    setDraft(null);
    setEditError('');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!editingId || !draft || !user?.id) return;
    setEditError('');
    setSaving(true);
    try {
      const missingEffect = sideEffects.find(
        (item) => draft.effects[item.key] !== 'no' && !isYesEffect(draft.effects[item.key])
      );
      if (missingEffect) {
        setEditError(`Answer yes or no for ${missingEffect.label}`);
        setSaving(false);
        return;
      }
      const payload = {
        userId: user.id,
        notes: draft.notes,
        privateNotes: draft.privateNotes,
        buyAgain: Number(draft.buyAgain),
      };
      for (const key of EFFECT_KEYS) {
        payload[key] = isYesEffect(draft.effects[key]) ? draft.effects[key] : 0;
      }
      const result = await updateLog(editingId, payload);
      if (selectedDate) {
        const refreshed = await getLogs(user.id, '', selectedDate);
        setLogs(refreshed.logs || []);
      } else {
        setLogs(result.logs || []);
      }
      closeEditor();
    } catch (err) {
      setEditError(err.error || 'Unable to save changes');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container wide">
      <p className="brand">My Weed Buddy</p>
      <h1>Welcome</h1>
      <button type="button" onClick={onOpenConsumption}>
        Consumption
      </button>
      <button type="button" className="secondary" onClick={onOpenFriends}>
        Friends
      </button>
      {statsAccess ? (
        <>
          <button type="button" className="secondary" onClick={onOpenDashboard}>
            Stats dashboard
          </button>
          <button type="button" className="secondary" onClick={onOpenAdminQuery}>
            Database query
          </button>
        </>
      ) : null}
      <button type="button" className="secondary" onClick={onOpenAccount}>
        Account Details
      </button>
      <button type="button" className="secondary" onClick={onLogout}>
        Log out
      </button>

      <h2 className="log-heading">{headingText}</h2>
      {canEdit ? (
        <p className="subtitle log-edit-hint">
          Tap a session to edit notes, private notes, side effects, or buy again.
        </p>
      ) : null}
      <div className="form-group">
        <label htmlFor="homeStrain">Search by strain</label>
        <StrainPicker
          id="homeStrain"
          strains={strains}
          value={strain}
          onSelect={(name) => {
            setStrain(name);
            if (name) setSelectedDate('');
          }}
          allowOther={false}
          placeholder="Search your logged strains"
          emptyLabel={strains.length ? 'No matching logged strains' : 'No logged strains yet'}
        />
      </div>
      {strain ? (
        <button type="button" className="secondary" onClick={() => setStrain('')}>
          Show all sessions
        </button>
      ) : null}

      <div className="form-group">
        <button
          type="button"
          className="disclosure-toggle"
          aria-expanded={calendarOpen}
          aria-controls="sessionCalendar"
          onClick={() => setCalendarOpen((open) => !open)}
        >
          <span>{selectedDate ? `Date: ${formatDateKey(selectedDate)}` : 'Search by date'}</span>
          <span className="disclosure-caret" aria-hidden="true">
            {calendarOpen ? '▾' : '▸'}
          </span>
        </button>
        {calendarOpen ? (
          <div id="sessionCalendar">
            <SessionCalendar
              key={user.themeColor || 'green'}
              dates={sessionDates}
              selectedDate={selectedDate}
              onSelectDate={(date) => {
                setSelectedDate(date);
                setStrain('');
                setCalendarOpen(false);
              }}
              onClear={() => setSelectedDate('')}
            />
          </div>
        ) : selectedDate ? (
          <button type="button" className="secondary" onClick={() => setSelectedDate('')}>
            Clear date filter
          </button>
        ) : null}
      </div>

      {logs.length === 0 ? (
        <p className="subtitle">
          {selectedDate
            ? `No sessions on ${formatDateKey(selectedDate)}.`
            : strain
              ? `No sessions saved yet for ${strain}.`
              : 'No sessions saved yet.'}
        </p>
      ) : (
        <ul className="log-list">
          {logs.map((log) => {
            const isEditing = canEdit && editingId === log.id;
            return (
              <li
                key={log.id}
                className={canEdit ? 'log-editable' : undefined}
                style={buyAgainHighlight(log.buyAgain, user.themeColor)}
              >
                {isEditing ? (
                  <form className="log-edit-panel" onSubmit={handleSave}>
                    <LogSummary log={log} />
                    <div className="form-group">
                      <label htmlFor={`notes-${log.id}`}>Notes</label>
                      <p className="field-note">Visible to friends.</p>
                      <textarea
                        id={`notes-${log.id}`}
                        value={draft.notes}
                        onChange={(e) => setDraft((current) => ({ ...current, notes: e.target.value }))}
                        rows={3}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor={`privateNotes-${log.id}`}>Private notes</label>
                      <p className="field-note">Only you can see these.</p>
                      <textarea
                        id={`privateNotes-${log.id}`}
                        value={draft.privateNotes}
                        onChange={(e) =>
                          setDraft((current) => ({ ...current, privateNotes: e.target.value }))
                        }
                        rows={3}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor={`buyAgain-${log.id}`}>Will you buy again?</label>
                      <input
                        id={`buyAgain-${log.id}`}
                        className="buy-again-slider"
                        type="range"
                        min="1"
                        max="5"
                        step="1"
                        value={draft.buyAgain}
                        onChange={(e) =>
                          setDraft((current) => ({ ...current, buyAgain: Number(e.target.value) }))
                        }
                      />
                      <div className="buy-again-marks">
                        <span>1 Never Again</span>
                        <span>3 Passable but Buyable</span>
                        <span>5 Always</span>
                      </div>
                      <p className="field-note">
                        Selected: {draft.buyAgain}
                        {BUY_AGAIN_LABELS[draft.buyAgain]
                          ? ` — ${BUY_AGAIN_LABELS[draft.buyAgain]}`
                          : ''}
                      </p>
                    </div>
                    <h3>Side effects</h3>
                    <p className="subtitle">Defaults to No. Click Yes only if you had that effect, then rate severity.</p>
                    {sideEffects.map((item) => (
                      <div className="form-group yn-row" key={item.key}>
                        <span>{item.label}</span>
                        <div className="yn-options">
                          <label>
                            <input
                              type="radio"
                              name={`${log.id}-${item.key}`}
                              value="yes"
                              checked={isYesEffect(draft.effects[item.key])}
                              onChange={() =>
                                setDraft((current) => ({
                                  ...current,
                                  effects: { ...current.effects, [item.key]: 3 },
                                }))
                              }
                            />{' '}
                            Yes
                          </label>
                          <label>
                            <input
                              type="radio"
                              name={`${log.id}-${item.key}`}
                              value="no"
                              checked={draft.effects[item.key] === 'no'}
                              onChange={() =>
                                setDraft((current) => ({
                                  ...current,
                                  effects: { ...current.effects, [item.key]: 'no' },
                                }))
                              }
                            />{' '}
                            No
                          </label>
                        </div>
                        {isYesEffect(draft.effects[item.key]) ? (
                          <div className="side-effect-severity">
                            <label htmlFor={`severity-${log.id}-${item.key}`}>Severity</label>
                            <input
                              id={`severity-${log.id}-${item.key}`}
                              className="buy-again-slider"
                              type="range"
                              min="1"
                              max="5"
                              step="1"
                              value={draft.effects[item.key]}
                              onChange={(e) =>
                                setDraft((current) => ({
                                  ...current,
                                  effects: {
                                    ...current.effects,
                                    [item.key]: Number(e.target.value),
                                  },
                                }))
                              }
                            />
                            <div className="buy-again-marks">
                              <span>1 Mild</span>
                              <span>3 Moderate</span>
                              <span>5 High</span>
                            </div>
                            <p className="field-note">
                              Selected: {draft.effects[item.key]}
                              {SIDE_EFFECT_SEVERITY_LABELS[draft.effects[item.key]]
                                ? ` — ${SIDE_EFFECT_SEVERITY_LABELS[draft.effects[item.key]]}`
                                : ''}
                            </p>
                          </div>
                        ) : null}
                      </div>
                    ))}
                    {editError ? <div className="error">{editError}</div> : null}
                    <div className="log-edit-actions">
                      <button type="submit" disabled={saving}>
                        {saving ? 'Saving...' : 'Save'}
                      </button>
                      <button type="button" className="secondary" onClick={closeEditor} disabled={saving}>
                        Cancel
                      </button>
                    </div>
                  </form>
                ) : (
                  <button
                    type="button"
                    className={`log-card-button${canEdit ? '' : ' log-card-static'}`}
                    onClick={() => openEditor(log)}
                    disabled={!canEdit}
                  >
                    <LogSummary log={log} />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default Home;
